import * as Sentry from '@sentry/nextjs'
import { SENTRY_CLIENT_IGNORE_ERRORS } from '@/lib/sentry-noise-filters'
import { scrubSentryEvent } from '@/lib/observability/scrub-invite-token'

// Only initialize Sentry in production or if DSN is explicitly set
const shouldInit = process.env.NEXT_PUBLIC_SENTRY_DSN && process.env.NODE_ENV === 'production'

if (shouldInit) {
  Sentry.init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
    tracesSampleRate: 1.0,
    debug: false,
    // Browser-extension noise (RANKSURF-8S) and handled deploy skew (RANKSURF-8G).
    ignoreErrors: SENTRY_CLIENT_IGNORE_ERRORS,
    // Sentry tunnel POSTs are same-origin so the browser attaches all cookies.
    // Supabase auth chunks can push the Cookie header past Vercel's 8 KB limit,
    // causing a 400. The tunnel doesn't need credentials — omit them.
    transportOptions: {
      fetchOptions: { credentials: 'omit' },
    },
    // An invite, claim or shared-report token never leaves the browser in an event, breadcrumb,
    // span or transaction name (AC-12, FU-191) — see src/lib/observability/scrub-invite-token.ts.
    beforeSend: (event) => tagPageTranslated(scrubSentryEvent(event)),
    beforeSendTransaction: (event) => tagPageTranslated(scrubSentryEvent(event)),
  })
}

// Chrome Translate puts `translated-ltr` / `translated-rtl` on <html>. It rewrites text nodes
// under React, which crashes `insertBefore`; the tag makes the residual crashes countable.
function tagPageTranslated<T extends Sentry.Event>(event: T | null): T | null {
  if (!event || typeof document === 'undefined') return event
  const cls = document.documentElement.classList
  const translated = cls.contains('translated-ltr') || cls.contains('translated-rtl')
  event.tags = { ...event.tags, page_translated: translated ? 'true' : 'false' }
  return event
}
